import { getDefaultRouteForRole } from './routeUtils';

const WELCOME_PENDING_KEY = 'onboarding-welcome-pending';

export function getStoredCountryId() {
  const id = localStorage.getItem('country-id');
  if (!id || id === 'undefined' || id === 'null') return null;
  return String(id);
}

export function markWelcomePending() {
  try {
    sessionStorage.setItem(WELCOME_PENDING_KEY, '1');
  } catch (_) {
    /* ignore */
  }
}

export function clearWelcomePending() {
  try {
    sessionStorage.removeItem(WELCOME_PENDING_KEY);
  } catch (_) {
    /* ignore */
  }
}

export function isWelcomePending() {
  try {
    return sessionStorage.getItem(WELCOME_PENDING_KEY) === '1';
  } catch (_) {
    return false;
  }
}

/**
 * A subscription record returned by the API.
 * Empty objects and locally invented flags do not count.
 */
export function isConfirmedSubscription(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  if (value.package_id != null && value.package_id !== '') return true;
  if (value.id != null && value.id !== '') return true;
  if (value.package_type) return true;
  if (value.is_trial === true || value.is_trial === 1) return true;
  if (value.status === true || value.status === 1 || value.status === 'active') return true;
  return false;
}

/** Brand setup done — username exists on the store (backend source of truth). */
export function isOnboardingComplete(store) {
  return Boolean(store?.username);
}

/**
 * Country explicitly stored on the account.
 * Phone dial-code inference and localStorage alone are not enough.
 */
export function hasExplicitStoreCountry(store) {
  if (!store || typeof store !== 'object') return false;
  const nested = store.country;
  const id = nested?.id ?? store.country_id ?? nested?.country_id ?? null;
  return id !== undefined && id !== null && id !== '' && id !== 'undefined' && id !== 'null';
}

/**
 * Package/trial already subscribed on the account.
 * Only API subscription fields count — not needsMainSettings / isMaltyBranches.
 */
export function hasStoreSubscription(store) {
  if (!store || typeof store !== 'object') return false;
  if (isConfirmedSubscription(store.current_subscription)) return true;
  if (isConfirmedSubscription(store.subscription)) return true;
  if (isConfirmedSubscription(store.active_subscription)) return true;
  const history = store.subscription_history;
  return Array.isArray(history) && history.some(isConfirmedSubscription);
}

/**
 * Align ephemeral local flags with backend store state.
 * Flags are UI hints only; route guards do not trust them.
 */
export function syncOnboardingFlagsFromStore(store) {
  if (!store || typeof store !== 'object') return;

  try {
    if (isOnboardingComplete(store)) {
      localStorage.removeItem('needsMainSettings');
      clearWelcomePending();
      return;
    }

    if (hasStoreSubscription(store)) {
      localStorage.setItem('needsMainSettings', 'true');
      const packageType = store.current_subscription?.package_type;
      if (packageType && !localStorage.getItem('isMaltyBranches')) {
        localStorage.setItem('isMaltyBranches', packageType);
      }
      clearWelcomePending();
      return;
    }

    localStorage.removeItem('needsMainSettings');
    localStorage.removeItem('isMaltyBranches');
  } catch (_) {
    /* ignore */
  }
}

/**
 * Next route for vendor onboarding based on backend store + subscription.
 *
 * Order: packages (with the welcome block) → continue-to-settings → main-setting → dashboard.
 * Account creation is not completion. Country is chosen on register, not on a welcome page.
 *
 * @param {{ role?: string, store?: object|null, context?: 'entry'|'packages'|'vendor' }} opts
 * @returns {string}
 */
export function resolveVendorOnboardingPath({ role, store, context = 'entry' } = {}) {
  if (role === 'marketer') {
    return '/welcome-marketer';
  }

  if (role && role !== 'business' && role !== 'mall') {
    return getDefaultRouteForRole(role);
  }

  if (isOnboardingComplete(store)) {
    return '/vendor/dashboard';
  }

  if (!hasStoreSubscription(store)) {
    return '/subscription-packages';
  }

  // Package confirmed, brand not finished.
  if (context === 'vendor') {
    return '/vendor/main-setting';
  }

  return '/continue-to-settings';
}

/**
 * Where an already-authenticated user should land (login/register bounce / `/`).
 */
export function resolveAuthenticatedEntryPath({ role, store } = {}) {
  if (role === 'marketer') {
    return '/welcome-marketer';
  }
  if (role === 'sub_business') {
    return getDefaultRouteForRole(role);
  }
  return resolveVendorOnboardingPath({ role, store, context: 'entry' });
}
